import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { Role } from '../enum/role';
import { PATH_CONFIG } from 'src/environments/paths.config';

type RolPermiso = {
  codigoModulo: string;
  codigoAcciones: string | null;
};

@Injectable({
  providedIn: 'root'
})
export class VariablesService {

  private _currentUserRole: Role = Role.NO_EXISTE;
  public get currentUserRole(): Role { return this._currentUserRole; }
  public set currentUserRole(value: Role) { this._currentUserRole = value; }

  private _paths: any = environment.paths;
  public get paths(): any { return this._paths; }
  public set paths(value: any) { this._paths = value; }

  private _nombreCliente: string = '';
  public get nombreCliente(): string { return this._nombreCliente; }
  public set nombreCliente(value: string) { this._nombreCliente = value; }

  private _idCliente: number = 0;
  public get idCliente(): number { return this._idCliente; }
  public set idCliente(value: number) { this._idCliente = value; }

  private _idUsuario: number = 0;
  public get idUsuario(): number { return this._idUsuario; }
  public set idUsuario(value: number) { this._idUsuario = value; }

  private _idAlmacen: number = 0;
  public get idAlmacen(): number {
    return this._idAlmacen;
  }
  public set idAlmacen(value: number) {
    this._idAlmacen = value;
  }

  private _usuario: string = '';
  public get usuario(): string { return this._usuario; }
  public set usuario(value: string) { this._usuario = value; }

  private _celular: number = 0;
  public get celular(): number { return this._celular; }
  public set celular(value: number) { this._celular = value; }

  private _email: string = '';
  public get email(): string { return this._email; }
  public set email(value: string) { this._email = value; }

  private _isJumpCube: boolean = false;
  public get isJumpCube(): boolean { return this._isJumpCube; }
  public set isJumpCube(value: boolean) { this._isJumpCube = value; }

  public get datacliente() {
    return localStorage.getItem('client') ? JSON.parse(localStorage.getItem('client')!) : null;
  }
  public set datacliente(value: any) { localStorage.setItem('client', JSON.stringify(value)); }

  /** Permisos del usuario: array de {codigoModulo, codigoAcciones} */
  private _rol: RolPermiso[] = [];

  /** Y = superadmin, tiene acceso a todo sin validar permisos */
  private _superAdmin: boolean = false;
  /** Solo true para el superadmin; no depende de módulos/roles asignados en BD */
  public get isSuperAdmin(): boolean { return this._superAdmin; }

  private _loaded = false;

  constructor(private router: Router) {}

  // ── Permisos ────────────────────────────────────────────────────────────────

  /** Carga los datos desde localStorage si aún no se ha hecho (útil en guards) */
  ensureLoaded() {
    if (!this._loaded) this.deserializeJson();
  }

  /** Devuelve true si el usuario tiene acceso al módulo indicado */
  hasModulo(codigoModulo: string): boolean {
    if (this._superAdmin) return true;
    return this._rol.some(r => r.codigoModulo === codigoModulo);
  }

  /** Devuelve true si el usuario no tiene ningún módulo/rol asignado */
  sinRolAsignado(): boolean {
    if (this._superAdmin) return false;
    return (
      this._currentUserRole === Role.NO_EXISTE ||
      (this._currentUserRole === Role.ADMIN && this._rol.length === 0)
    );
  }

  /** Devuelve true si el usuario tiene una acción específica dentro de un módulo */
  hasAccion(codigoModulo: string, codigoAccion: string): boolean {
    if (this._superAdmin) return true;
    return this._rol.some(
      r => r.codigoModulo === codigoModulo && r.codigoAcciones === codigoAccion
    );
  }

  /**
   * Devuelve PATH_CONFIG filtrado por los módulos/sub-módulos que el usuario tiene permiso.
   * Usa esto para construir el menú de navegación dinámicamente.
   * Los ítems marcados con `soloSuperAdmin` sólo se muestran al superadmin y quedan
   * fuera del sistema de módulos/roles: asignarlos por Roles/Permisos no los habilita.
   * Los marcados con `oculto` no se muestran a nadie (temporal, ej. un módulo
   * todavía en construcción) — independiente de módulos/roles/superadmin.
   */
  getMenuFiltrado(): any[] {
    const visible = (item: any) =>
      !item.oculto &&
      (!item.soloSuperAdmin || this.isSuperAdmin) &&
      (!item.modulo || this.hasModulo(item.modulo));

    return PATH_CONFIG
      .filter(visible)
      .map(item => ({
        ...item,
        options: ((item.options ?? []) as any[]).filter(visible)
      }));
  }

  // ── Deserialización ─────────────────────────────────────────────────────────

  deserializeJson() {
    if (localStorage.getItem('data')) {
      const data = JSON.parse(localStorage.getItem('data')!);
      if (data) {
        this.nombreCliente    = (data.nombre1 ?? '') + ' ' + (data.apellido1 ?? '');
        this.idCliente        = data.idCliente     ?? 0;
        this.idUsuario        = data.idUsuario     ?? 0;
         this.idAlmacen = data.idAlmacen ? data.idAlmacen : 0;
        this.usuario          = data.nombreCompleto ?? '';
        this.email            = data.email         ?? '';
        this.celular          = data.celular        ?? 0;
        this.currentUserRole  = data.role ? data.role : (data.idRol ? Role.ADMIN : Role.NO_EXISTE);
        this._rol             = Array.isArray(data.rol) ? data.rol : [];
        this._superAdmin      = data.superAdmin === 'Y';
      } else {
        this.currentUserRole = Role.NO_EXISTE;
        this._rol            = [];
        this._superAdmin     = false;
      }
    }

    this.isJumpCube = !!environment.jumpCube;
    this._loaded = true;
  }

  // ── Sesión ──────────────────────────────────────────────────────────────────

  deleteLocalVaribles() {
    localStorage.removeItem('client');
    localStorage.removeItem('data');
    localStorage.removeItem('gran-type');
    localStorage.removeItem('registerClient');
  }

  logout() {
    this.deleteLocalVaribles();
    this.router.navigate(this.idCliente === 0 ? ['/'] : ['/client']);
  }
}
